import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One fetch, one explicit state machine: idle → loading → success | error.
 *
 * Two behaviours are the whole point:
 *
 *  1. A failed refetch KEEPS the data it already had and sets `error`. Clicking
 *     to page 3 and failing must not wipe page 2 off the screen — the table
 *     shows the old rows with a red banner instead.
 *
 *  2. `state` is never left at "success" with empty data after a catch. The
 *     existing pages (UserDetails.loadData, and every mobile screen) catch,
 *     console.error, and leave data as [] — which renders as "this user has
 *     nothing", indistinguishable from a working empty account. That is the
 *     specific bug this hook exists to make impossible.
 *
 * Out-of-order responses are dropped by request sequence number, so a slow
 * page-1 response cannot overwrite a fast page-2 one.
 */
export default function useAdminResource(fetcher, deps = [], options = {}) {
  const { initialParams = {}, enabled = true, initialData = null } = options;

  const [data, setData] = useState(initialData);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const [params, setParamsRaw] = useState(initialParams);

  const seqRef = useRef(0);
  const hasDataRef = useRef(initialData != null);
  // Keep the latest fetcher without making it a dependency — callers pass an
  // inline arrow, which would otherwise re-run the effect on every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useCallback(
    async (overrideParams) => {
      if (!enabled) return;

      const seq = ++seqRef.current;
      setState("loading");
      setError(null);

      try {
        const result = await fetcherRef.current(overrideParams ?? params);
        if (seq !== seqRef.current) return; // a newer request already won

        const items = result?.items ?? result;
        setData(items);
        hasDataRef.current = true;
        if (result?.pagination) setMeta(result.pagination);
        setState("success");
        return result;
      } catch (err) {
        if (seq !== seqRef.current) return;

        // ⚠️ Do NOT clear data here — stale rows plus a banner beat a blank table.
        setError(err);
        setState("error");
      }
    },
    [enabled, params]
  );

  useEffect(() => {
    if (!enabled) return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, JSON.stringify(params), ...deps]);

  /** Merge params and reset to page 1 — changing a filter must not leave you
   *  stranded on a page number that no longer exists. */
  const setParams = useCallback((patch) => {
    setParamsRaw((prev) => {
      const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
      const onlyPageChanged =
        Object.keys(next).every((k) => k === "page" || next[k] === prev[k]) &&
        next.page !== prev.page;
      return onlyPageChanged ? next : { ...next, page: 1 };
    });
  }, []);

  return {
    data,
    meta,
    state,
    error,
    params,
    setParams,
    refetch: run,
    hasData: hasDataRef.current,
    dismissError: () => setError(null),
  };
}
